/** @file Mounts the runtime on a canvas: renderer, listeners, size, rAF loop, bindings, API, compiled sync. */
import { fontPage } from '@pose2d/api/render/fontPage';
import type { Id } from '@pose2d/format/types';
import { defaultFont } from '@pose2d/overlay/font/defaultFont';
import { createViewportRenderer } from '@pose2d/renderer-webgl/createViewportRenderer';
import type { ViewportRenderer } from '@pose2d/renderer-webgl/types';
import { pushContext } from '@pose2d/shortcuts/pushContext';
import type { FetchLike } from '../../api/createApiClient';
import { fetchImagePng } from '../../api/fetchImagePng';
import { dispatch } from '../../api/sync/dispatch';
import { registerEffective } from '../../shell/shortcuts/registerEffective';
import { docStore } from '../../stores/docStore';
import { installCompiledSync } from '../../stores/installCompiledSync';
import type { PendingCommand } from '../../stores/types';
import { createViewportRuntime, type ViewportRuntime } from './createViewportRuntime';
import { openingFit } from './openingFit';
import { viewportApi } from './viewportApi';
import { viewportHandlers } from './viewportHandlers';
import { viewportStore } from './viewportStore';
import { viewportTable } from './viewportTable';
import type { ViewportPointer } from './viewportTypes';

/** Browser services, injected so tests run in Node. */
export interface MountEnv {
  createRenderer: (canvas: HTMLCanvasElement) => ViewportRenderer;
  requestFrame: (cb: () => void) => number;
  cancelFrame: (id: number) => void;
  observeSize: (el: HTMLElement, cb: (width: number, height: number) => void) => () => void;
  dpr: () => number;
  fetch: FetchLike;
}

const BROWSER: MountEnv = {
  createRenderer: createViewportRenderer,
  requestFrame: (cb) => requestAnimationFrame(cb),
  cancelFrame: (id) => { cancelAnimationFrame(id); },
  observeSize: (el, cb) => {
    const ro = new ResizeObserver((entries) => { const box = entries[0]?.contentRect; if (box) cb(box.width, box.height); });
    ro.observe(el);
    return () => { ro.disconnect(); };
  },
  dpr: () => window.devicePixelRatio,
  fetch: (url, init) => fetch(url, init),
};

/** Canvas CSS pixels; Ctrl or Cmd count as `ctrl` (R-E1-10, R-E1-11). */
function sampleOf(canvas: HTMLCanvasElement, e: PointerEvent | WheelEvent): ViewportPointer {
  const r = canvas.getBoundingClientRect();
  return { sx: e.clientX - r.left, sy: e.clientY - r.top, button: e.button, ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey, alt: e.altKey };
}

type Listener = [string, (e: Event) => void, AddEventListenerOptions?];

/** Pointer listeners: only the pressing pointer drives a pan or gesture (a second touch or pen contact is ignored);
 *  a lost capture or a move with no button down (a release the window missed) cancels it; leaving un-hovers. */
function pointerListeners(canvas: HTMLCanvasElement, runtime: ViewportRuntime): Listener[] {
  let active: number | null = null;
  const other = (e: Event): boolean => active !== null && (e as PointerEvent).pointerId !== active;
  const drop = (): void => { active = null; runtime.cancel(); };
  return [
    ['pointerdown', (e) => {
      if (other(e)) return;
      active = (e as PointerEvent).pointerId;
      canvas.setPointerCapture(active);
      canvas.focus();
      runtime.pointer('down', sampleOf(canvas, e as PointerEvent));
    }],
    ['pointermove', (e) => {
      if (other(e)) return;
      if (active !== null && (e as PointerEvent).buttons === 0) drop();
      else runtime.pointer('move', sampleOf(canvas, e as PointerEvent));
    }],
    ['pointerup', (e) => { if (other(e)) return; active = null; runtime.pointer('up', sampleOf(canvas, e as PointerEvent)); }],
    ['pointercancel', (e) => { if (!other(e)) drop(); }],
    ['lostpointercapture', (e) => { if (active !== null && !other(e)) drop(); }],
    ['pointerleave', () => { if (active === null) runtime.cancel(); }],
  ];
}

/** Pointer, wheel, context-menu, focus and context-restore listeners (no key listener: shortcuts own the keyboard). */
function listen(canvas: HTMLCanvasElement, runtime: ViewportRuntime, restored: () => void): () => void {
  let pop: (() => void) | null = null;
  const on: Listener[] = [
    ...pointerListeners(canvas, runtime),
    ['wheel', (e) => { e.preventDefault(); const w = sampleOf(canvas, e as WheelEvent); runtime.wheel(w.sx, w.sy, (e as WheelEvent).deltaY); }, { passive: false }],
    ['contextmenu', (e) => { e.preventDefault(); }],
    ['focus', () => { pop ??= pushContext('viewport'); }],
    ['blur', () => { pop?.(); pop = null; }],
    ['webglcontextrestored', restored],
  ];
  for (const [type, fn, opts] of on) canvas.addEventListener(type, fn, opts);
  return () => { for (const [type, fn] of on) canvas.removeEventListener(type, fn); pop?.(); };
}

/** One batch through E8's dispatch (applied to the view before it returns, W3-10); a refusal becomes the notice.
 *  Returns what the optimistic apply created, name → id (W3-11), {} when refused at once. */
function commit(batch: PendingCommand[]): Readonly<Record<string, Id>> {
  const fail = (thrown: unknown): void => { viewportStore.setState({ notice: { kind: 'error', thrown } }); };
  try {
    const handle = dispatch(batch);
    handle.done.catch(fail);
    return handle.created;
  } catch (thrown) {
    fail(thrown);
    return {};
  }
}

/** Everything the viewport needs while mounted; the disposer undoes it in reverse order. */
export function mountViewport(canvas: HTMLCanvasElement, env: MountEnv = BROWSER): () => void {
  const font = defaultFont();
  const stopSync = installCompiledSync();
  const runtime = createViewportRuntime({
    renderer: env.createRenderer(canvas), font, fontImage: fontPage(font), commit,
    fetchPng: (sha) => { const { projectId } = docStore.getState(); return projectId === null ? Promise.resolve(null) : fetchImagePng(env.fetch, projectId, sha); },
  });
  // The renderer rebuilds its GPU state on restore (its listener runs first) but paints only when asked: resize marks dirty.
  let size: [number, number] = [0, 0];
  const stopListen = listen(canvas, runtime, () => { runtime.resize(size[0], size[1], env.dpr()); });
  // The first real size also frames a just-opened project (once; never over a camera the person moved).
  const stopSize = env.observeSize(canvas, (w, h) => { size = [w, h]; runtime.resize(w, h, env.dpr()); openingFit.sized(w, h); });
  const unregister = registerEffective('viewport', viewportTable(), viewportHandlers(runtime));
  const detach = viewportApi.attach(runtime);
  let frameId = 0;
  const tick = (): void => { runtime.frame(); frameId = env.requestFrame(tick); };
  frameId = env.requestFrame(tick);
  return () => { env.cancelFrame(frameId); detach(); unregister(); stopSize(); openingFit.sized(0, 0); stopListen(); runtime.dispose(); stopSync(); };
}
